import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdStockLocationsId,
  getAdminV1StoresStoreIdInventory,
  getAdminV1OrgProductsProductIdStock,
  getAdminV1StoresStoreIdInventoryMovements,
  getAdminV1StoresStoreIdInventoryVariantsVariantId,
  getAdminV1StoresStoreIdStockLocations,
  patchAdminV1OrgProductsIdVariantsVariantId,
  patchAdminV1StoresStoreIdStockLocationsId,
  postAdminV1StoresStoreIdInventoryAdjust,
  postAdminV1StoresStoreIdStockLocations,
  putAdminV1StoresStoreIdInventoryReorderPoint,
  type GetAdminV1StoresStoreIdInventory200,
  type GetAdminV1StoresStoreIdInventoryMovements200,
  type GetAdminV1StoresStoreIdInventoryMovementsParams,
  type InventoryItem,
  type InventoryLevel,
  type PatchAdminV1StoresStoreIdStockLocationsIdBody,
  type PostAdminV1StoresStoreIdInventoryAdjustBody,
  type PostAdminV1StoresStoreIdStockLocationsBody,
  type PutAdminV1StoresStoreIdInventoryReorderPointBody,
  type StockLocation,
  type StoreVariantStock,
} from '@nucleus-commerce/api-client/admin';

/**
 * Level list params as `listParams` builds them from the route's search: `q`, `sort`, the flat
 * filter params (`locationId=a,b`, `lowStock=true`, `available_lte=0`), `cursor`, `limit`.
 */
export type InventoryListParams = ListParams;

/**
 * Stock of one store: its locations (a bounded `{ items }` list, default first), the level of
 * every tracked variant per location, and the ledger every change writes.
 */
export const inventoryApi = {
  listLocations: (storeId: string): Promise<StockLocation[]> =>
    getAdminV1StoresStoreIdStockLocations({ storeId }).then((r) => r.items),
  createLocation: (
    storeId: string,
    body: PostAdminV1StoresStoreIdStockLocationsBody,
  ): Promise<StockLocation> => postAdminV1StoresStoreIdStockLocations({ storeId }, body),
  updateLocation: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdStockLocationsIdBody,
  ): Promise<StockLocation> => patchAdminV1StoresStoreIdStockLocationsId({ storeId, id }, body),
  removeLocation: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdStockLocationsId({ storeId, id }),

  levels: (
    storeId: string,
    params: InventoryListParams,
  ): Promise<GetAdminV1StoresStoreIdInventory200> =>
    getAdminV1StoresStoreIdInventory({ storeId }, params),
  adjust: (
    storeId: string,
    body: PostAdminV1StoresStoreIdInventoryAdjustBody,
  ): Promise<InventoryLevel> => postAdminV1StoresStoreIdInventoryAdjust({ storeId }, body),
  setReorderPoint: (
    storeId: string,
    body: PutAdminV1StoresStoreIdInventoryReorderPointBody,
  ): Promise<InventoryLevel> => putAdminV1StoresStoreIdInventoryReorderPoint({ storeId }, body),
  movements: (
    storeId: string,
    params: GetAdminV1StoresStoreIdInventoryMovementsParams,
  ): Promise<GetAdminV1StoresStoreIdInventoryMovements200> =>
    getAdminV1StoresStoreIdInventoryMovements({ storeId }, params),
  /** One variant at every location of the store, tracked or not. */
  item: (storeId: string, variantId: string): Promise<InventoryItem> =>
    getAdminV1StoresStoreIdInventoryVariantsVariantId({ storeId, variantId }),
  /** Totals per store and variant of a master product; absent pairs read as zero. */
  productStock: (productId: string): Promise<StoreVariantStock[]> =>
    getAdminV1OrgProductsProductIdStock({ productId }).then((r) => r.items),
  /** Tracking is a master variant field; the inventory page offers to switch it on. */
  setTracking: (productId: string, variantId: string, trackInventory: boolean) =>
    patchAdminV1OrgProductsIdVariantsVariantId({ id: productId, variantId }, { trackInventory }),
};
